import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Fireworks } from "@/components/game/fireworks/Fireworks.js";
import { ActiveGameMenu } from "@/components/game/menu/ActiveGameMenu.js";
import { WinMenu } from "@/components/game/menu/WinMenu.js";
import type { LobbyStatus } from "@rosette/ts/src/business/game/controller/status/LobbyStatus.js";
import { LoadingMenu } from "@/components/game/menu/LoadingMenu.js";
import { useAccessibility } from "@rosette/web_components/src/accessibility/AccessibilityContext.js";
import {
    useArePlayersConnected, useGameClientControls, useGameController,
} from "@/components/game/GameUIContext.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import { GameStatus } from "@/components/game/menu/ingame/GameStatus.js";
import { GameMenuResources } from "@/components/game/menu/GameMenuResources.js";
import { useAssetLoader } from "@/components/assets/AssetContext.js";
import { IntroMenu } from "@/components/game/menu/IntroMenu.js";
import type { UseMutationResult } from "@tanstack/react-query";
import type { ClientError } from "@rosette/ts/src/business/game/error/ClientError.js";
import type { APIEarnedAchievement } from "@rosette/ts/src/business/api/api_schema.js";
import { ErrorMenu } from "@/components/game/menu/ErrorMenu.js";
import type { Optional } from "@rosette/ts/src/util/Optional.js";
import { EndGameState } from "@rosette/ts/src/royalur/engine/state/EndGameState.js";
import { MenuCornerButton, MenuDarkOverlay, MenuLayer } from "@/components/game/menu/MenuLayout.js";
import { AbortGameMenu } from "@/components/game/menu/AbortGameMenu.js";
import { RulesMenu } from "@/components/game/menu/RulesMenu.js";
import { PreferencesMenu } from "@/components/game/menu/PreferencesMenu.js";
import { Path } from "@rosette/ts/src/util/Path.js";
import { GameEndReason } from "@rosette/ts/src/business/game/GameEndReason.js";
import { MenuType } from "@rosette/web_components/src/game/menu/MenuType.js";
import type { MenuContainerProps } from "@/components/game/menu/MenuContainer.js";
import { MenuContainer } from "@/components/game/menu/MenuContainer.js";
import { useGameAnalysis } from "@/app/(api)/api/getGame/useGame.js";
import { useAnalysisAchievementToasts } from "@/utils/achievements/useAnalysisAchievementToasts.js";


export function useGameMenuResources(): GameMenuResources {
    const assetLoader = useAssetLoader();
    return useMemo(() => {
        return new GameMenuResources(assetLoader);
    }, [assetLoader]);
}


interface InGameButtonsProps {
    isMobile: boolean;
    lobbyStatus: LobbyStatus;
    statusText: string | null;
    statusDotDotDot: boolean;
    activeMenu: MenuType | null;
    setPreferredMenu: (menu: MenuType | null) => void;
    toggleMenu: () => void;
    toggleAbortMenu: () => void;
}


function InGameButtons({
    isMobile, lobbyStatus, statusText, statusDotDotDot,
    activeMenu, setPreferredMenu, toggleMenu, toggleAbortMenu,
}: InGameButtonsProps) {

    if (!lobbyStatus.isGameAvailable())
        return (<></>);

    return (
        <>
            <MenuCornerButton
                side="left"
                isActive={activeMenu !== null && activeMenu !== MenuType.ABORT}
                isMobile={isMobile}
                onClick={toggleMenu} />

            <MenuCornerButton
                side="right"
                isActive={activeMenu === MenuType.ABORT}
                isMobile={isMobile}
                onClick={toggleAbortMenu} />

            {activeMenu === null && (
                <GameStatus
                    className="absolute top-0 left-0 w-full z-98"
                    statusText={statusText}
                    dotDotDot={statusDotDotDot}
                    activeMenu={activeMenu}
                    setPreferredMenu={setPreferredMenu} />
            )}
        </>
    );
}


export interface GameMenuProps {
    isMobile: boolean;
    isGameWon: boolean;
    didYourPlayerWin: boolean;
    error: Optional<ClientError>;
    uploadGame: UseMutationResult<APIEarnedAchievement[], Error, void, unknown>;
    isPathActive: boolean;
    setPathActive: (active: boolean) => void;
}

export function GameMenu({
    isMobile, isGameWon, didYourPlayerWin, error, uploadGame,
    isPathActive, setPathActive,
}: GameMenuProps) {
    const controller = useGameController();
    const clientControls = useGameClientControls();
    const players = useRune(controller.getPlayers());
    const resources = useGameMenuResources();

    const game = useRune(controller.getGame());
    const lobbyStatus = useRune(controller.getLobbyStatus());
    const endReason = lobbyStatus.getEndReason();
    const { p1Connected, p2Connected } = useArePlayersConnected(lobbyStatus, players);
    const isGameAvailable = lobbyStatus.isGameAvailable();
    const isConnected = lobbyStatus.isConnected();
    const isRemote = lobbyStatus.isRemote();
    const isSpectator = (isRemote && players.getYourPlayer() === null);

    const { game: apiGame, analysis: analysisOverview } = useGameAnalysis(lobbyStatus.getCompleteGameID());

    // Toast achievements that were granted during the game's analysis.
    useAnalysisAchievementToasts(
        lobbyStatus.getCompleteGameID(), analysisOverview, players.getYourPlayer(),
    );

    const [preferredMenu, setPreferredMenu] = useState<MenuType | null>(
        isPathActive ? MenuType.PATH
            : (game.isFinished() ? MenuType.WIN : MenuType.INTRO),
    );

    // The logic to decide the menu that will actually be displayed.
    let menu = preferredMenu;
    let menuCanBeClosed = true;
    if (preferredMenu !== MenuType.ABORT) {
        if (error.isPresent()) {
            menu = MenuType.ERROR;
            menuCanBeClosed = false;
        } else if (!isGameAvailable) {
            menu = MenuType.LOADING;
            menuCanBeClosed = false;
        }
    }
    const isMenuActive = (menu !== null);

    // Sync isPathActive with menu = PATH.
    useEffect(() => {
        setPathActive(menu === MenuType.PATH);
    }, [setPathActive, menu]);

    const closeMenu = useCallback(() => {
        setPreferredMenu(null);
    }, [setPreferredMenu]);

    const openMenu = useCallback(() => {
        if (isGameWon) {
            setPreferredMenu(MenuType.WIN);
        } else if (isGameAvailable) {
            setPreferredMenu(MenuType.GAME);
        }
    }, [isGameWon, isGameAvailable, setPreferredMenu]);

    const toggleMenu = useCallback(() => {
        if (!preferredMenu || preferredMenu === MenuType.ABORT) {
            openMenu();
        } else if (menuCanBeClosed) {
            closeMenu();
        }
    }, [preferredMenu, menuCanBeClosed, closeMenu, openMenu]);

    const toggleAbortMenu = useCallback(() => {
        if (preferredMenu === null || preferredMenu === MenuType.WIN) {
            setPreferredMenu(MenuType.ABORT);
        } else if (menuCanBeClosed) {
            closeMenu();
        }
    }, [preferredMenu, setPreferredMenu, menuCanBeClosed, closeMenu]);

    let continuePath: Path | null = null;
    const finishedGameID = lobbyStatus.getCompleteGameID();
    if (finishedGameID) {
        continuePath = new Path(`/g/${finishedGameID}`);
    }

    // Play the game start sound!
    const playedStartSoundRef = useRef<boolean>(isGameAvailable);
    if (isGameAvailable && !playedStartSoundRef.current) {
        playedStartSoundRef.current = true;
        resources.getGameStartSound().play(true);
    } else if (!isGameAvailable) {
        playedStartSoundRef.current = false;
    }

    let statusText: string | null = null;
    let statusDotDotDot: boolean = false;
    if (isGameAvailable) {
        if (!isConnected) {
            statusText = "Reconnecting";
            statusDotDotDot = true;
        } else if (!p1Connected) {
            statusText = `${players.getPlayer1().getShortName()} has disconnected`;
            statusDotDotDot = true;
        } else if (!p2Connected) {
            statusText = `${players.getPlayer2().getShortName()} has disconnected`;
            statusDotDotDot = true;
        } else if (players.isSpectator(lobbyStatus)) {
            statusText = "You are a spectator";
        }
    }

    // Show the menu when the game has been won.
    useEffect(() => {
        return controller.subscribeToActiveDirective((directive) => {
            if (directive.state instanceof EndGameState) {
                setPreferredMenu(MenuType.WIN);
            }
        });
    }, [controller, setPreferredMenu]);

    // Dodgy way to close the menu when the game is restarted.
    const lastGameWon = useRef<boolean>(isGameWon);
    useEffect(() => {
        if (lastGameWon.current && !isGameWon) {
            closeMenu();
        }
        lastGameWon.current = isGameWon;
    }, [isGameWon, closeMenu]);

    const menuContentRef = useRef<HTMLDivElement | null>(null);
    const menuContainerProps: MenuContainerProps = {
        contentRef: menuContentRef, closeMenu, isMobile,
    };

    const accessibility = useAccessibility();

    return (
        <div>
            <MenuLayer isMobile={isMobile}>
                {!isMenuActive && (
                    <InGameButtons
                        isMobile={isMobile}
                        lobbyStatus={lobbyStatus}
                        statusText={statusText}
                        statusDotDotDot={statusDotDotDot}
                        activeMenu={menu}
                        setPreferredMenu={setPreferredMenu}
                        toggleMenu={toggleMenu}
                        toggleAbortMenu={toggleAbortMenu} />
                )}
            </MenuLayer>

            <div>
                {isMenuActive && menu !== MenuType.PATH && (
                    <MenuDarkOverlay />
                )}

                <MenuLayer isMobile={isMobile}>
                    {isMenuActive && (
                        <InGameButtons
                            isMobile={isMobile}
                            lobbyStatus={lobbyStatus}
                            statusText={statusText}
                            statusDotDotDot={statusDotDotDot}
                            activeMenu={menu}
                            setPreferredMenu={setPreferredMenu}
                            toggleMenu={toggleMenu}
                            toggleAbortMenu={toggleAbortMenu} />
                    )}

                    <AnimatePresence mode="wait">
                        {menu === MenuType.LOADING && (
                            <MenuContainer key="loading" {...menuContainerProps}>
                                <LoadingMenu
                                    contentRef={menuContentRef}
                                    lobbyStatus={lobbyStatus} />
                            </MenuContainer>
                        )}

                        {menu === MenuType.ERROR && (
                            <MenuContainer key="error" {...menuContainerProps}>
                                <ErrorMenu
                                    contentRef={menuContentRef}
                                    error={error.get()} />
                            </MenuContainer>
                        )}

                        {menu === MenuType.INTRO && (
                            <MenuContainer key="intro" {...menuContainerProps}>
                                <IntroMenu
                                    contentRef={menuContentRef}
                                    lobbyStatus={lobbyStatus}
                                    settings={game.getRules().getSettings()}
                                    players={players}
                                    closeMenu={closeMenu} />
                            </MenuContainer>
                        )}

                        {menu === MenuType.GAME && (
                            <MenuContainer key="game" {...menuContainerProps}>
                                <ActiveGameMenu
                                    contentRef={menuContentRef}
                                    showPreferences={() => setPreferredMenu(MenuType.PREFERENCES)}
                                    showRules={() => setPreferredMenu(MenuType.RULES)}
                                    backToHome={() => clientControls.abortGame()} />
                            </MenuContainer>
                        )}

                        {menu === MenuType.PREFERENCES && (
                            <MenuContainer key="preferences" {...menuContainerProps}>
                                <PreferencesMenu
                                    contentRef={menuContentRef}
                                    back={() => setPreferredMenu(MenuType.GAME)} />
                            </MenuContainer>
                        )}

                        {menu === MenuType.RULES && (
                            <MenuContainer key="rules" {...menuContainerProps}>
                                <RulesMenu
                                    contentRef={menuContentRef}
                                    back={() => setPreferredMenu(MenuType.GAME)}
                                    showPath={() => setPreferredMenu(MenuType.PATH)} />
                            </MenuContainer>
                        )}

                        {menu === MenuType.PATH && (
                            <MenuContainer key="rules" {...menuContainerProps}>
                                <div ref={menuContentRef}>
                                    {/* Empty, but this is still needed so the menu  */}
                                    {/* can be closed when the user clicks anywhere. */}
                                </div>
                            </MenuContainer>
                        )}

                        {menu === MenuType.WIN && (
                            <MenuContainer key="win" {...menuContainerProps}>
                                <WinMenu
                                    apiGame={apiGame}
                                    analysisOverview={analysisOverview}
                                    contentRef={menuContentRef}
                                    uploadGame={uploadGame}
                                    isMenuActive={isMenuActive}
                                    continuePath={continuePath} />
                            </MenuContainer>
                        )}

                        {menu === MenuType.ABORT && (
                            <MenuContainer key="abort" {...menuContainerProps}>
                                <AbortGameMenu
                                    contentRef={menuContentRef}
                                    leaveTitle={isGameWon ? "Exit the Game" : "Leave your Game"}
                                    leaveMessage={(
                                        isGameWon || isRemote
                                            ? "Hopefully you enjoyed your game!"
                                            : "Once you leave, you will not be able to recover this game.")}
                                    leaveButtonText="Leave game"
                                    hasGameEnded={isGameWon}
                                    isSpectator={isSpectator}
                                    closeMenu={closeMenu} />
                            </MenuContainer>
                        )}
                    </AnimatePresence>
                </MenuLayer>

                {(
                    isMenuActive && isGameWon && didYourPlayerWin
                    && !accessibility.useReducedMotion()
                    && endReason !== GameEndReason.LEFT_BEFORE_BEGIN
                ) && (
                    <Fireworks isMobile={isMobile} />
                )}
            </div>
        </div>
    );
}
